import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HealthRing } from "../components/HealthRing";
import { StatusBadge } from "../components/StatusBadge";
import { RequestDialog } from "../components/RequestDialog";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fcfa, money, shortDate, healthColor, PRODUCTS } from "../lib/format";
import { ArrowLeft, MapPin, Loader2, Plus, ClipboardCheck, Wrench, FileText, Receipt, Bed, Bath, Ruler, CheckCircle2, Upload, Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  structural: "Structural", roof: "Roof", electrical: "Electrical", plumbing: "Plumbing",
  walls: "Walls", floor: "Floor", doors_windows: "Doors & Windows", painting: "Painting",
  garden: "Garden", security: "Security",
};
const CONDITION_FIELDS = Object.keys(CATEGORY_LABELS);

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "manager"].includes(user.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeReq, setActiveReq] = useState(null);

  const load = () => api.get(`/properties/${id}`).then((res) => setData(res.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!data) return <p>Property not found.</p>;

  const { property: p, inspections, requests, work_orders, quotations, invoices, contract,
          rooms = [], projects = [], defects = [], boqs = [], documents = [] } = data;
  const prod = PRODUCTS[p.product] || PRODUCTS.CARE;
  const conditions = p.health_conditions || {};
  const cur = p.currency || "XAF";
  const M = (x) => money(x, cur);

  return (
    <div className="fade-up">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between mb-8">
        <div>
          <span className={`inline-block text-[0.6rem] uppercase tracking-widest text-white px-2 py-0.5 rounded-full ${prod.color}`}>{prod.label}</span>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-3">{p.property_name}</h1>
          <p className="font-mono text-sm text-muted-foreground mt-1">{p.code}</p>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" aria-hidden="true" /> {p.address}, {p.city}{p.country ? `, ${p.country}` : ""}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium" data-testid="property-currency">{cur}</span>
            {p.bedrooms != null && <span className="flex items-center gap-1"><Bed className="h-4 w-4" aria-hidden="true" /> {p.bedrooms}</span>}
            {p.bathrooms != null && <span className="flex items-center gap-1"><Bath className="h-4 w-4" aria-hidden="true" /> {p.bathrooms}</span>}
            {p.building_area_m2 != null && <span className="flex items-center gap-1"><Ruler className="h-4 w-4" aria-hidden="true" /> {p.building_area_m2} m²</span>}
          </div>
          {isAdmin && <p className="text-sm text-muted-foreground mt-2">Owner: <span className="text-foreground font-medium">{p.owner_name}</span></p>}
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <HealthRing score={p.health_score} />
            <p className="overline text-muted-foreground mt-2">Property Health</p>
          </div>
        </div>
      </div>

      {/* Contract / Diaspora banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4 border-border">
          <p className="overline text-muted-foreground">Maintenance</p>
          <div className="mt-2">{contract ? <StatusBadge status="ACTIVE" /> : <span className="text-sm text-muted-foreground">No contract</span>}</div>
          {contract && <p className="text-sm mt-2 font-medium">{M(contract.monthly_fee)}/mo</p>}
        </Card>
        <Card className="p-4 border-border">
          <p className="overline text-muted-foreground">Last Inspection</p>
          <p className="font-medium mt-2">{shortDate(p.last_inspection)}</p>
        </Card>
        <Card className="p-4 border-border">
          <p className="overline text-muted-foreground">Open Requests</p>
          <p className="font-heading text-2xl font-bold mt-1">{requests.filter((r) => r.status !== "COMPLETED" && r.status !== "CANCELLED").length}</p>
        </Card>
        <Card className="p-4 border-border">
          <p className="overline text-muted-foreground">Outstanding</p>
          <p className="font-heading text-2xl font-bold mt-1">{M(invoices.reduce((s, i) => s + (i.balance || 0), 0))}</p>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Health</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests ({requests.length})</TabsTrigger>
          <TabsTrigger value="work" data-testid="tab-work">Work Orders ({work_orders.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">Quotations ({quotations.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects ({projects.length})</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspections ({inspections.length})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documents ({documents.length})</TabsTrigger>
        </TabsList>

        {/* Overview / Health */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 border-border lg:col-span-2">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-bold tracking-tight">Condition Breakdown</h3>
                {isAdmin && <InspectionDialog propertyId={id} conditions={conditions} onDone={load} />}
              </div>
              {Object.keys(conditions).length === 0 ? (
                <p className="text-sm text-muted-foreground">No inspection recorded yet.</p>
              ) : (
                <div className="space-y-4">
                  {CONDITION_FIELDS.filter((k) => conditions[k] != null).map((k) => (
                    <div key={k}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{CATEGORY_LABELS[k]}</span>
                        <span style={{ color: healthColor(conditions[k]) }} className="font-semibold">{conditions[k]}</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${conditions[k]}%`, background: healthColor(conditions[k]) }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-6 border-border">
              <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Recommended Priorities</h3>
              {p.priorities?.length ? (
                <div className="space-y-3">
                  {p.priorities.map((pr) => (
                    <div key={pr.priority} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/60">
                      <span className="h-6 w-6 shrink-0 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center">{pr.priority}</span>
                      <div>
                        <p className="font-medium text-sm">{pr.label}</p>
                        <p className="text-xs text-muted-foreground">Condition score {pr.score}/100</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Property in good health — no urgent priorities.</p>}
              {!isAdmin && (
                <Link to={`/requests/new?property=${id}`} className="block mt-5">
                  <Button className="w-full rounded-full hover:bg-primary/90" data-testid="request-from-property">
                    <Wrench className="h-4 w-4 mr-2" aria-hidden="true" /> Request Maintenance
                  </Button>
                </Link>
              )}
            </Card>
          </div>
          <AIReport property={p} onDone={load} />
        </TabsContent>

        {/* Requests */}
        <TabsContent value="requests">
          <SectionHeader title="Maintenance Requests" action={!isAdmin && (
            <Link to={`/requests/new?property=${id}`}><Button size="sm" className="rounded-full" data-testid="add-request-tab"><Plus className="h-4 w-4 mr-1" /> New</Button></Link>
          )} />
          {requests.length === 0 ? <Empty icon={Wrench} text="No requests yet" /> : (
            <div className="space-y-3">
              {requests.map((r) => (
                <Card key={r.id} onClick={() => setActiveReq(r)} className="p-4 border-border cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between gap-4" data-testid={`prop-request-${r.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium">{r.category} <span className="text-xs text-muted-foreground">· {r.priority}</span></p>
                    <p className="text-sm text-muted-foreground truncate">{r.description}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground hidden sm:block">{shortDate(r.created_at)}</span>
                    <StatusBadge status={r.status} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Work Orders */}
        <TabsContent value="work">
          <SectionHeader title="Work Orders" action={isAdmin && <WorkOrderDialog propertyId={id} onDone={load} />} />
          {work_orders.length === 0 ? <Empty icon={Wrench} text="No work orders" /> : (
            <div className="space-y-3">
              {work_orders.map((w) => (
                <Card key={w.id} className="p-4 border-border" data-testid={`work-order-${w.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium">{w.task}</p>
                      <p className="text-sm text-muted-foreground">{w.assigned_to} · {M(w.estimated_cost)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={w.status} />
                      {isAdmin && <WorkOrderUpdate wo={w} onDone={load} />}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Progress value={w.completion_percent} className="h-2" />
                    <span className="text-xs text-muted-foreground w-10 text-right">{w.completion_percent}%</span>
                  </div>
                  <SiteProgress wo={w} isAdmin={isAdmin} onDone={load} />
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Quotations */}
        <TabsContent value="quotes">
          <SectionHeader title="Quotations" action={isAdmin && <QuotationDialog propertyId={id} onDone={load} />} />
          {quotations.length === 0 ? <Empty icon={FileText} text="No quotations" /> : (
            <div className="space-y-3">
              {quotations.map((q) => (
                <Card key={q.id} className="p-5 border-border" data-testid={`quotation-${q.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">{q.title}</p>
                      <p className="text-xs font-mono text-muted-foreground">{q.number}</p>
                    </div>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    {q.items.map((it, i) => (
                      <div key={i} className="flex justify-between text-muted-foreground">
                        <span>{it.description} × {it.quantity} {it.unit}</span><span>{M(it.line_total)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-border font-heading font-bold text-base">
                      <span>Total</span><span>{M(q.total_amount)}</span>
                    </div>
                  </div>
                  {!isAdmin && q.status === "SENT" && (
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" className="rounded-full hover:bg-primary/90" data-testid={`approve-quote-${q.id}`}
                        onClick={async () => { await api.patch(`/quotations/${q.id}?status=APPROVED`); toast.success("Quotation approved"); load(); }}>Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`reject-quote-${q.id}`}
                        onClick={async () => { await api.patch(`/quotations/${q.id}?status=REJECTED`); toast("Quotation rejected"); load(); }}>Reject</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Invoices */}
        <TabsContent value="invoices">
          <SectionHeader title="Invoices" action={isAdmin && <InvoiceDialog propertyId={id} onDone={load} />} />
          {invoices.length === 0 ? <Empty icon={Receipt} text="No invoices" /> : (
            <div className="space-y-3">
              {invoices.map((inv) => (
                <Card key={inv.id} className="p-5 border-border flex items-center justify-between gap-4" data-testid={`invoice-${inv.id}`}>
                  <div>
                    <p className="font-medium">{inv.description}</p>
                    <p className="text-xs font-mono text-muted-foreground">{inv.number}</p>
                    <p className="text-sm text-muted-foreground mt-1">Balance: {M(inv.balance)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold">{M(inv.amount)}</p>
                    <StatusBadge status={inv.status} className="mt-1" />
                    {!isAdmin && inv.balance > 0 && (
                      <Button size="sm" className="rounded-full mt-2 hover:bg-primary/90" data-testid={`pay-invoice-${inv.id}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.post("/payments/checkout", { invoice_id: inv.id, origin_url: window.location.origin });
                            window.location.href = data.checkout_url;
                          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment"); }
                        }}>Pay online</Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Projects */}
        <TabsContent value="projects">
          <SectionHeader title="Projects & BOQ" action={isAdmin && (
            <div className="flex gap-2"><BOQDialog propertyId={id} projects={projects} currency={cur} onDone={load} /><ProjectDialog propertyId={id} onDone={load} /></div>
          )} />
          {projects.length === 0 ? <Empty icon={FileText} text="No projects yet" /> : (
            <div className="space-y-3">
              {projects.map((pr) => (
                <Card key={pr.id} className="p-5 border-border" data-testid={`project-${pr.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium">{pr.name} <span className="text-xs text-muted-foreground">· {pr.project_type}</span></p>
                      <p className="text-xs font-mono text-muted-foreground">{pr.number}</p>
                      {pr.description && <p className="text-sm text-muted-foreground mt-1">{pr.description}</p>}
                      {pr.budget != null && <p className="text-sm mt-1">Budget: <span className="font-medium">{M(pr.budget)}</span></p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={pr.status} />
                      {isAdmin && <ProjectStatusDialog project={pr} onDone={load} />}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Progress value={pr.progress_percent} className="h-2" />
                    <span className="text-xs text-muted-foreground w-10 text-right">{pr.progress_percent}%</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {boqs.length > 0 && (
            <div className="mt-6 space-y-3">
              <h4 className="font-heading font-bold tracking-tight">Bills of Quantities</h4>
              {boqs.map((b) => (
                <Card key={b.id} className="p-5 border-border" data-testid={`boq-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{b.title} <span className="text-xs font-mono text-muted-foreground">{b.number}</span></p>
                    <p className="font-heading font-bold">{M(b.total_amount)}</p>
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {b.items.map((it, i) => (
                      <div key={i} className="flex justify-between"><span>{it.description} × {it.quantity} {it.unit}</span><span>{M(it.amount)}</span></div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Inspections + Defects */}
        <TabsContent value="inspections">
          <SectionHeader title="Inspections & Defects" />
          {inspections.length === 0 ? <Empty icon={ClipboardCheck} text="No inspections yet" /> : (
            <div className="space-y-4">
              {inspections.map((ins) => {
                const insDefects = defects.filter((d) => d.inspection_id === ins.id);
                return (
                  <Card key={ins.id} className="p-5 border-border" data-testid={`inspection-${ins.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Inspection · {shortDate(ins.inspection_date)}</p>
                        <p className="text-sm text-muted-foreground">by {ins.inspector_name} · score {ins.overall_score}/100</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-2xl font-bold" style={{ color: healthColor(ins.overall_score) }}>{ins.overall_score}</span>
                        {isAdmin && <DefectDialog inspectionId={ins.id} onDone={load} />}
                      </div>
                    </div>
                    {ins.recommendations && <p className="text-sm text-muted-foreground mt-2 italic">"{ins.recommendations}"</p>}
                    {insDefects.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2">
                        {insDefects.map((d) => (
                          <div key={d.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`defect-${d.id}`}>
                            <div className="min-w-0">
                              <span className="font-medium">{d.category}</span>
                              {d.room && <span className="text-muted-foreground"> · {d.room}</span>}
                              <p className="text-muted-foreground truncate">{d.description}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {d.estimated_cost != null && <span className="text-xs text-muted-foreground">{M(d.estimated_cost)}</span>}
                              <StatusBadge status={d.status} />
                              <StatusBadge status={d.severity} />
                              {isAdmin && <DefectStatus defect={d} onDone={load} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="docs">
          <SectionHeader title="Documents" action={<DocumentDialog propertyId={id} onDone={load} />} />
          {documents.length === 0 ? <Empty icon={FileText} text="No documents uploaded" /> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {documents.map((doc) => (
                <a key={doc.id} href={fileUrl(doc.storage_path)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:shadow-md transition-shadow" data-testid={`document-${doc.id}`}>
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{doc.name}</p>
                    <p className="text-xs text-muted-foreground">{doc.document_type} · {shortDate(doc.created_at)}</p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <RequestDialog request={activeReq} open={!!activeReq} onClose={() => setActiveReq(null)} isAdmin={isAdmin} onUpdated={load} />
    </div>
  );
}

function AIReport({ property, onDone }) {
  const [loading, setLoading] = useState(false);
  const report = property.ai_report;
  const generate = async () => {
    setLoading(true);
    try {
      await api.post(`/properties/${property.id}/ai-report`);
      toast.success("AI assessment ready");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate");
    } finally { setLoading(false); }
  };
  return (
    <Card className="mt-6 p-6 border-border bg-gradient-to-br from-secondary/40 to-transparent">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />
          <h3 className="font-heading text-lg font-bold tracking-tight">AI Property Advisor</h3>
        </div>
        <Button size="sm" onClick={generate} disabled={loading} className="rounded-full hover:bg-primary/90" data-testid="ai-report-button">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {report ? "Regenerate" : "Generate assessment"}
        </Button>
      </div>
      {report ? (
        <div className="mt-4">
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" data-testid="ai-report-text">{(report.text || "").replace(/[*#`]/g, "")}</p>
          <p className="text-xs text-muted-foreground mt-4">Generated {shortDate(report.generated_at)}{report.by ? ` · ${report.by}` : ""} · Powered by Claude</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          Get an AI assessment of this property's condition and the top actions to take — based on its latest
          inspection scores, open defects and maintenance requests.
        </p>
      )}
    </Card>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-lg font-bold tracking-tight">{title}</h3>
      {action}
    </div>
  );
}
function Empty({ icon: Icon, text }) {
  return (
    <Card className="p-10 text-center border-dashed">
      <Icon className="h-8 w-8 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </Card>
  );
}

/* ---------- Admin dialogs ---------- */
function InspectionDialog({ propertyId, conditions, onDone }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState(() => {
    const init = {};
    CONDITION_FIELDS.forEach((k) => (init[k] = conditions[k] ?? 80));
    return init;
  });
  const [rec, setRec] = useState("");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/properties/${propertyId}/inspections`, {
        conditions: vals, estimated_repair_cost: cost ? Number(cost) : 0, recommendations: rec,
      });
      toast.success("Inspection recorded");
      setOpen(false);
      onDone();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="add-inspection-button"><ClipboardCheck className="h-4 w-4 mr-1" /> New Inspection</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Record Inspection</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {CONDITION_FIELDS.map((k) => (
            <div key={k} className="flex items-center gap-3">
              <Label className="w-32 text-sm">{CATEGORY_LABELS[k]}</Label>
              <Input type="number" min={0} max={100} value={vals[k]} onChange={(e) => setVals({ ...vals, [k]: Number(e.target.value) })} data-testid={`condition-${k}`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Estimated repair cost (FCFA)</Label>
            <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="inspection-cost" />
          </div>
          <div className="space-y-1.5">
            <Label>Recommendations</Label>
            <Textarea rows={2} value={rec} onChange={(e) => setRec(e.target.value)} data-testid="inspection-rec" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full rounded-full hover:bg-primary/90" data-testid="save-inspection-button">{saving ? "Saving…" : "Save Inspection"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderDialog({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [task, setTask] = useState("");
  const [cost, setCost] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const save = async () => {
    await api.post("/work-orders", { property_id: propertyId, task, priority, estimated_cost: cost ? Number(cost) : null });
    toast.success("Work order created"); setOpen(false); setTask(""); setCost(""); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="add-work-order-button"><Plus className="h-4 w-4 mr-1" /> New</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New Work Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Task</Label><Textarea rows={2} value={task} onChange={(e) => setTask(e.target.value)} data-testid="wo-task" /></div>
          <div className="space-y-1.5"><Label>Estimated cost</Label><Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="wo-cost" /></div>
          <Button onClick={save} disabled={!task} className="w-full rounded-full hover:bg-primary/90" data-testid="save-work-order-button">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderUpdate({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(wo.completion_percent);
  const [status, setStatus] = useState(wo.status);
  const save = async () => {
    await api.patch(`/work-orders/${wo.id}`, { completion_percent: Number(pct), status });
    toast.success("Updated"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`update-wo-${wo.id}`}>Update</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Update Work Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Completion %</Label><Input type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} data-testid="wo-pct" /></div>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="wo-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["OPEN", "IN_PROGRESS", "COMPLETED", "CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} className="w-full rounded-full hover:bg-primary/90" data-testid="save-wo-update">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuotationDialog({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit: "unit", unit_price: 0 }]);
  const [labour, setLabour] = useState(0);
  const [transport, setTransport] = useState(0);
  const [mgmt, setMgmt] = useState(0);
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const total = items.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price), 0) + Number(labour) + Number(transport) + Number(mgmt);
  const save = async () => {
    await api.post("/quotations", {
      property_id: propertyId, title,
      items: items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })),
      labour_cost: Number(labour), transport_cost: Number(transport), management_fee: Number(mgmt),
    });
    toast.success("Quotation created"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="add-quotation-button"><Plus className="h-4 w-4 mr-1" /> New</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New Quotation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quote-title" /></div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`quote-item-desc-${i}`} />
              <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`quote-item-qty-${i}`} />
              <Input className="col-span-4" type="number" placeholder="Unit price" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`quote-item-price-${i}`} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems([...items, { description: "", quantity: 1, unit: "unit", unit_price: 0 }])} data-testid="add-quote-item">+ Add item</Button>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Labour</Label><Input type="number" value={labour} onChange={(e) => setLabour(e.target.value)} data-testid="quote-labour" /></div>
            <div><Label className="text-xs">Transport</Label><Input type="number" value={transport} onChange={(e) => setTransport(e.target.value)} data-testid="quote-transport" /></div>
            <div><Label className="text-xs">Mgmt fee</Label><Input type="number" value={mgmt} onChange={(e) => setMgmt(e.target.value)} data-testid="quote-mgmt" /></div>
          </div>
          <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t border-border"><span>Total</span><span>{fcfa(total)}</span></div>
          <Button onClick={save} disabled={!title} className="w-full rounded-full hover:bg-primary/90" data-testid="save-quotation-button">Create Quotation</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STAGE_LABELS = { BEFORE: "Before", IN_PROGRESS: "In Progress", AFTER: "After" };

function SiteProgress({ wo, isAdmin, onDone }) {
  const photos = wo.photos || [];
  const grouped = { BEFORE: [], IN_PROGRESS: [], AFTER: [] };
  photos.forEach((p) => { (grouped[p.stage] || grouped.IN_PROGRESS).push(p); });
  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <p className="overline text-muted-foreground flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" aria-hidden="true" /> Site Progress</p>
        {isAdmin && <SitePhotoDialog wo={wo} onDone={onDone} />}
      </div>
      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No site photos yet.</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4">
          {["BEFORE", "IN_PROGRESS", "AFTER"].map((stage) => (
            <div key={stage}>
              <p className="text-xs font-semibold mb-2 text-foreground">{STAGE_LABELS[stage]}</p>
              {grouped[stage].length === 0 ? (
                <div className="h-24 rounded-lg border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">—</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {grouped[stage].map((p) => (
                    <img key={p.id} src={fileUrl(p.storage_path)} alt={p.note || stage}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="h-24 w-full object-cover rounded-lg border border-border" data-testid={`site-photo-${p.id}`} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SitePhotoDialog({ wo, onDone }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("IN_PROGRESS");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [storagePath, setStoragePath] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStoragePath(data.storage_path);
      setPreview(URL.createObjectURL(file));
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const save = async () => {
    try {
      await api.post(`/work-orders/${wo.id}/photos`, { stage, storage_path: storagePath, note });
      toast.success("Site photo added");
      setOpen(false); setStoragePath(null); setPreview(null); setNote("");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add photo");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`add-site-photo-${wo.id}`}><Camera className="h-4 w-4 mr-1" /> Add photo</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Add Site Photo</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger data-testid="site-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BEFORE">Before</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="AFTER">After</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Photo</Label>
            <label className="flex flex-col items-center justify-center h-32 rounded-lg border border-dashed border-border cursor-pointer hover:bg-secondary transition-colors overflow-hidden" data-testid="site-upload-label">
              {preview ? <img src={preview} alt="preview" className="h-full w-full object-cover" /> : (
                uploading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> :
                <><Upload className="h-6 w-6 text-muted-foreground" aria-hidden="true" /><span className="text-xs text-muted-foreground mt-1">Upload photo</span></>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="site-photo-input" />
            </label>
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="site-note-input" />
          </div>
          <Button onClick={save} disabled={!storagePath || uploading} className="w-full rounded-full hover:bg-primary/90" data-testid="save-site-photo-button">Add to Work Order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const save = async () => {
    await api.post("/invoices", { property_id: propertyId, description, amount: Number(amount) });
    toast.success("Invoice created"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="add-invoice-button"><Plus className="h-4 w-4 mr-1" /> New</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="invoice-desc" /></div>
          <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="invoice-amount" /></div>
          <Button onClick={save} disabled={!description || !amount} className="w-full rounded-full hover:bg-primary/90" data-testid="save-invoice-button">Create Invoice</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDialog({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", project_type: "TRANSFORM", description: "", budget: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    await api.post("/projects", { property_id: propertyId, name: form.name, project_type: form.project_type,
      description: form.description, budget: form.budget ? Number(form.budget) : null });
    toast.success("Project created"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="add-project-button"><Plus className="h-4 w-4 mr-1" /> Project</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name" /></div>
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
              <SelectTrigger data-testid="project-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["BUILD", "TRANSFORM", "CARE"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="project-desc" /></div>
          <div className="space-y-1.5"><Label>Budget</Label><Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="project-budget" /></div>
          <Button onClick={save} disabled={!form.name} className="w-full rounded-full hover:bg-primary/90" data-testid="save-project-button">Create Project</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectStatusDialog({ project, onDone }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(project.status);
  const [pct, setPct] = useState(project.progress_percent);
  const save = async () => {
    await api.put(`/projects/${project.id}/status`, { status, progress_percent: Number(pct) });
    toast.success("Project updated"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`update-project-${project.id}`}>Update</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Update Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["PLANNED", "IN_PROGRESS", "COMPLETED", "CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Progress %</Label><Input type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} data-testid="project-progress" /></div>
          <Button onClick={save} className="w-full rounded-full hover:bg-primary/90" data-testid="save-project-status">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BOQDialog({ propertyId, projects, currency, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("");
  const [items, setItems] = useState([{ description: "", unit: "unit", quantity: 1, rate: 0 }]);
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const total = items.reduce((s, it) => s + Number(it.quantity) * Number(it.rate), 0);
  const save = async () => {
    await api.post("/boqs", { property_id: propertyId, project_id: projectId || null, title,
      items: items.map((it) => ({ ...it, quantity: Number(it.quantity), rate: Number(it.rate) })) });
    toast.success("BOQ created"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid="add-boq-button"><Plus className="h-4 w-4 mr-1" /> BOQ</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New Bill of Quantities</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="boq-title" /></div>
          {projects.length > 0 && (
            <div className="space-y-1.5"><Label>Link to project (optional)</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="boq-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`boq-item-desc-${i}`} />
              <Input className="col-span-2" placeholder="Unit" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} />
              <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
              <Input className="col-span-3" type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems([...items, { description: "", unit: "unit", quantity: 1, rate: 0 }])} data-testid="add-boq-item">+ Add item</Button>
          <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t border-border"><span>Total</span><span>{money(total, currency)}</span></div>
          <Button onClick={save} disabled={!title} className="w-full rounded-full hover:bg-primary/90" data-testid="save-boq-button">Create BOQ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DefectDialog({ inspectionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", severity: "MEDIUM", room: "", description: "", estimated_cost: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    await api.post("/defects", { inspection_id: inspectionId, category: form.category, severity: form.severity,
      room: form.room || null, description: form.description, estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : null });
    toast.success("Defect logged"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid={`add-defect-${inspectionId}`}><Plus className="h-4 w-4 mr-1" /> Defect</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Log Defect</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="defect-category" /></div>
            <div className="space-y-1.5"><Label>Room</Label><Input value={form.room} onChange={(e) => set("room", e.target.value)} data-testid="defect-room" /></div>
          </div>
          <div className="space-y-1.5"><Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="defect-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="defect-description" /></div>
          <div className="space-y-1.5"><Label>Estimated cost</Label><Input type="number" value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} data-testid="defect-cost" /></div>
          <Button onClick={save} disabled={!form.category || !form.description} className="w-full rounded-full hover:bg-primary/90" data-testid="save-defect-button">Log Defect</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DefectStatus({ defect, onDone }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(defect.status);
  const save = async () => {
    await api.put(`/defects/${defect.id}`, { status });
    toast.success("Defect updated"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`update-defect-${defect.id}`}>Edit</Button></DialogTrigger>
      <DialogContent className="max-w-xs">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Defect Status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="defect-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["OPEN", "IN_PROGRESS", "COMPLETED"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={save} className="w-full rounded-full hover:bg-primary/90" data-testid="save-defect-status">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocumentDialog({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("Title Deed");
  const [storagePath, setStoragePath] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStoragePath(data.storage_path); setFileName(file.name);
      if (!name) setName(file.name);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const save = async () => {
    try {
      await api.post("/documents", { property_id: propertyId, name, document_type: docType, storage_path: storagePath });
      toast.success("Document added"); setOpen(false); setStoragePath(null); setName(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="add-document-button"><Plus className="h-4 w-4 mr-1" /> Upload</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Add Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Title Deed", "Building Permit", "Survey Plan", "Contract", "Invoice", "Photo", "General"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="doc-name" /></div>
          <div className="space-y-1.5"><Label>File</Label>
            <label className="flex items-center justify-center h-20 rounded-lg border border-dashed border-border cursor-pointer hover:bg-secondary transition-colors" data-testid="doc-upload-label">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> :
                storagePath ? <span className="text-sm text-primary font-medium truncate px-2">{fileName}</span> :
                <><Upload className="h-5 w-5 text-muted-foreground mr-2" aria-hidden="true" /><span className="text-sm text-muted-foreground">Choose file</span></>}
              <input type="file" className="hidden" onChange={handleUpload} data-testid="doc-file-input" />
            </label>
          </div>
          <Button onClick={save} disabled={!storagePath || !name || uploading} className="w-full rounded-full hover:bg-primary/90" data-testid="save-document-button">Add Document</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
